import { ClaimStripePaymentDTO, ClaimStripePaymentTotalByOrderDTO, ClaimStripePaymentTotalDTO } from '@reposit/api-client';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { appConfig } from '../../appConfig';
import { penceToPounds } from '../../utils/common.utils';
import Button from '../Button';
import Select from '../FormFields/Select';
import IdentifierValue from '../IdentifierValue';
import InlineTable from '../InlineTable';
import Modal from '../Modal';
import RepositCard from '../Reposit/RepositCard';
import { Table, Tbody, Td, Thead, Tr } from '../Table';
import { Header3, Header4, Label, P2 } from '../Typography';
import { ALL_ORDERS_FILTER, CLAIM_PROPOSALS_FILTER, OrderHandlingMode } from './constants';
import {
  BuiltOrderFilterOption,
  buildOrderFilterOptions,
  ClaimPaymentOrderFilterOption,
  formatEmptyPaymentScope,
  formatOrderLabel,
} from './order-filter';

interface ClaimPaymentsProps {
  payments: ClaimStripePaymentDTO[];
  stripePaymentTotal?: ClaimStripePaymentTotalDTO;
  stripePaymentTotalsByOrder?: ClaimStripePaymentTotalByOrderDTO[];
  isLoading: boolean;
  error?: string;
  activeOrderId?: string;
  activeOrderStatusId?: string;
  lastActiveOrderId?: string;
  summaryOrderAmountRemaining?: number;
  summaryOrderAmountRemainingOrderId?: string;
  summaryOrderFilterOptions?: ClaimPaymentOrderFilterOption[];
  orderFilterOptions?: ClaimPaymentOrderFilterOption[];
  showAmountRemainingAccuracyWarning?: boolean;
  onSummaryOrderChange?: (orderId?: string, includePaymentPlanInterest?: boolean) => void;
}

interface LabelMap {
  [value: string]: string;
}

const productLabels: LabelMap = {
  REPOSIT: 'Reposit',
  CLAIM: 'Claim',
  CLAIM_PROPOSAL: 'Claim proposal',
  ARBITRATION: 'Arbitration',
  TOP_UP: 'Top up',
  ARBITRATION_ADMIN_FEE: 'Arbitration admin fee',
};

const collectionMethodLabels: LabelMap = {
  PAYMENT_PLAN_INSTALMENT: 'Payment plan instalment',
  AUTO_CHARGE: 'Auto charge',
  ORDER_CUSTOMER_PAYMENT: 'In app payment',
};

const statusLabels: LabelMap = {
  SUCCESS: 'Success',
  FAILURE: 'Failure',
  REFUNDED: 'Refunded',
  DISPUTED: 'Disputed',
};

const orderStatusLabels: LabelMap = {
  DRAFT: 'Draft',
  PENDING: 'Pending',
  COMPLETE: 'Complete',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded',
};

const TableScrollContainer = styled.div`
  overflow-x: auto;
`;

const PaymentSummaryTableContainer = styled.div`
  padding: 0 35px 20px;
`;

const CardMessage = styled.div`
  padding: 0 35px 20px;

  ${P2} {
    margin-bottom: 0;
  }
`;

const OrderWarning = styled.div`
  background: ${(props) => props.theme.colors.warning};
  border-radius: 4px;
  box-sizing: border-box;
  margin: 0 35px 20px;
  padding: 12px 16px;
`;

const OrderWarningText = styled(P2)`
  color: #fff;
  margin-bottom: 0;
`;

const OrderFilter = styled.div`
  align-items: center;
  display: flex;
  flex: 0 1 300px;
  margin-left: auto;
  max-width: 320px;
  min-width: 150px;
`;

const OrderFilterLabel = styled(Label)`
  flex: 0 0 auto;
  margin: 0 16px 0 0;
`;

const ActionCell = styled(Td)`
  text-align: right;
`;

const AmountSecondaryText = styled.span`
  color: ${(props) => props.theme.colors.body2};
  display: block;
  font-size: 13px;
`;

const IdentifierLink = styled.a`
  color: #0000ee;
  text-decoration: underline;
`;

const ModalHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
`;

const ModalTitle = styled(Header3)`
  margin-bottom: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${(props) => props.theme.colors.body2};
  cursor: pointer;
  font-size: 28px;
  line-height: 1;
  outline: none;
`;

const SectionTitle = styled(Header4)`
  margin-bottom: 12px;
`;

const DetailTable = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.dividerLight};
`;

const TwoColumnDetailTable = styled(DetailTable)`
  display: grid;
  grid-column-gap: 36px;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-column-gap: 36px;
  grid-row-gap: 36px;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  margin-top: 36px;
`;

const SectionPanel = styled.div`
  background: ${(props) => props.theme.colors.bgAccent};
  border: 1px solid ${(props) => props.theme.colors.dividerLight};
  border-radius: 6px;
  padding: 18px 20px 20px;

  ${DetailTable} {
    border-top: none;
  }
`;

const DetailTableRow = styled.div`
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.colors.dividerLight};
  display: flex;
  justify-content: space-between;
  min-height: 32px;
  padding: 4px 0;
`;

const DetailLabel = styled(P2)`
  flex: 0 0 28%;
  margin-bottom: 0;
  min-width: 0;
  padding-right: 16px;
`;

const DetailValue = styled.span`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: bold;
  line-height: 1.45;
  max-width: 72%;
  text-align: right;
  word-break: break-word;
`;

const formatDateTime = (date?: Date | string) => {
  return date ? moment(date).format('DD/MM/YYYY HH:mm:ss') : '-';
};

const formatMoney = (value?: number) => {
  return value || value === 0 ? `£${penceToPounds(value)}` : '-';
};

const formatCount = (value?: number) => {
  return value || value === 0 ? `${value}` : '-';
};

const formatCapturedTotal = (value: number | undefined, isNotApplicable: boolean) => {
  return isNotApplicable ? 'N/A' : formatMoney(value);
};

const formatHandlingMode = (value?: string | null) => {
  return toSentenceCase(value || OrderHandlingMode.MANUAL);
};

const toSentenceCase = (value: string) => {
  const lowerValue = value.replace(/[_-]+/g, ' ').toLowerCase();
  return lowerValue.charAt(0).toUpperCase() + lowerValue.slice(1);
};

const formatEnumLabel = (value: string | null | undefined, labels: LabelMap) => {
  if (!value) {
    return '-';
  }

  return labels[value] || toSentenceCase(value);
};

const formatCollectionMethod = (value: string | null | undefined) => {
  return value ? formatEnumLabel(value, collectionMethodLabels) : 'Unknown';
};

const sortPayments = (payments: ClaimStripePaymentDTO[]) => {
  return [...payments].sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf());
};

export const getLatestPaymentOrderId = (payments: ClaimStripePaymentDTO[]) => {
  const latestPayment = sortPayments(payments)[0];
  return latestPayment ? latestPayment.orderId : undefined;
};

const getSummaryOrderIdForSelectedOption = (
  option: BuiltOrderFilterOption | undefined,
  payments: ClaimStripePaymentDTO[],
  activeOrderId?: string,
  defaultOrderId?: string,
) => {
  if (!option) {
    return undefined;
  }

  if (activeOrderId && option.orderIds.includes(activeOrderId)) {
    return activeOrderId;
  }

  if (defaultOrderId && option.orderIds.includes(defaultOrderId)) {
    return defaultOrderId;
  }

  const latestOrderId = getLatestPaymentOrderId(payments.filter((payment) => option.orderIds.includes(payment.orderId)));
  return latestOrderId || option.orderIds[0];
};

const getPaymentTotalForSelectedFilter = (
  selectedOrderFilter: string,
  selectedOrderOption: BuiltOrderFilterOption | undefined,
  stripePaymentTotal: ClaimStripePaymentTotalDTO | undefined,
  stripePaymentTotalsByOrder: ClaimStripePaymentTotalByOrderDTO[],
) => {
  if (selectedOrderFilter === ALL_ORDERS_FILTER) {
    return stripePaymentTotal;
  }

  if (!selectedOrderOption || selectedOrderOption.value === CLAIM_PROPOSALS_FILTER || selectedOrderOption.orderIds.length !== 1) {
    return undefined;
  }

  return stripePaymentTotalsByOrder.find((total) => total.orderId === selectedOrderOption.orderIds[0]);
};

const getStripeChargeUrl = (chargeId?: string | null) => {
  if (!chargeId || !appConfig.stripeDashboardUrl) {
    return undefined;
  }

  return `${appConfig.stripeDashboardUrl.replace(/\/$/, '')}/payments/${encodeURIComponent(chargeId)}`;
};

const PaymentIdentifier: React.FC<{ value?: string | null; fallback?: string; href?: string }> = ({ value, fallback, href }) => {
  const identifier = value || fallback;

  if (!identifier) {
    return <>-</>;
  }

  if (href) {
    return (
      <IdentifierLink href={href} rel="noopener noreferrer" target="_blank" title={identifier}>
        <IdentifierValue>{identifier}</IdentifierValue>
      </IdentifierLink>
    );
  }

  return <IdentifierValue title={identifier}>{identifier}</IdentifierValue>;
};

interface DetailRow {
  label: string;
  value?: string | null;
  fallback?: string;
  isIdentifier?: boolean;
  href?: string;
}

interface DetailSection {
  title: string;
  rows: DetailRow[];
}

const hasRowValue = (row: DetailRow) => !!(row.value || row.fallback);

const getCollectionIdRow = (payment: ClaimStripePaymentDTO) => {
  const retryablePaymentRow = {
    label: 'Retryable Payment ID',
    value: payment.retryablePaymentId,
    isIdentifier: true,
  };
  const paymentPlanInstalmentRow = {
    label: 'Payment Plan Instalment ID',
    value: payment.paymentPlanInstalmentId,
    isIdentifier: true,
  };
  const paymentPlanRow = {
    label: 'Payment Plan ID',
    value: payment.paymentPlanId,
    isIdentifier: true,
  };
  const autoChargeRow = {
    label: 'Auto Charge ID',
    value: payment.autoChargeId,
    isIdentifier: true,
  };
  const orderCustomerActionRow = {
    label: 'Order Customer Action ID',
    value: payment.orderCustomerActionId,
    isIdentifier: true,
  };
  const methodRows: { [method: string]: DetailRow[] } = {
    PAYMENT_PLAN_INSTALMENT: [
      paymentPlanInstalmentRow,
      paymentPlanRow,
      retryablePaymentRow,
      autoChargeRow,
      orderCustomerActionRow,
    ],
    AUTO_CHARGE: [autoChargeRow, retryablePaymentRow, orderCustomerActionRow, paymentPlanInstalmentRow, paymentPlanRow],
    ORDER_CUSTOMER_PAYMENT: [
      orderCustomerActionRow,
      retryablePaymentRow,
      autoChargeRow,
      paymentPlanInstalmentRow,
      paymentPlanRow,
    ],
  };
  const collectionRows: DetailRow[] = methodRows[payment.collectionMethod || ''] || [
    retryablePaymentRow,
    paymentPlanInstalmentRow,
    paymentPlanRow,
    autoChargeRow,
    orderCustomerActionRow,
  ];

  return collectionRows.find(hasRowValue);
};

const DetailRows: React.FC<{ rows: DetailRow[] }> = ({ rows }) => (
  <>
    {rows.map((row) => (
      <DetailTableRow key={row.label}>
        <DetailLabel>{row.label}</DetailLabel>
        <DetailValue>
          {row.isIdentifier ? (
            <PaymentIdentifier value={row.value} fallback={row.fallback} href={row.href} />
          ) : (
            row.value || row.fallback || '-'
          )}
        </DetailValue>
      </DetailTableRow>
    ))}
  </>
);

const PaymentDetailsModal: React.FC<{ payment: ClaimStripePaymentDTO; onDismiss: () => void }> = ({ payment, onDismiss }) => {
  const paymentRows: DetailRow[] = [
    { label: 'Status', value: formatEnumLabel(payment.chargeStatusId, statusLabels) },
    { label: 'Amount', value: formatMoney(payment.amount) },
    { label: 'Amount Captured', value: formatMoney(payment.amountCaptured) },
    { label: 'Paid At', value: formatDateTime(payment.createdAt) },
    { label: 'Product', value: formatEnumLabel(payment.productId, productLabels) },
    { label: 'Collection Method', value: formatCollectionMethod(payment.collectionMethod) },
    { label: 'Payer Name', value: payment.payerName },
    { label: 'Payer Email', value: payment.payerEmail },
  ];

  const stripeRows: DetailRow[] = [
    {
      label: 'Charge ID',
      value: payment.stripeChargeId,
      isIdentifier: true,
      href: getStripeChargeUrl(payment.stripeChargeId),
    },
    { label: 'Payment Intent ID', value: payment.stripePaymentIntentId, isIdentifier: true },
    { label: 'Stripe Customer ID', value: payment.stripeCustomerId, isIdentifier: true },
    { label: 'Invoice ID', value: payment.invoiceId, isIdentifier: true },
    { label: 'Credit Note ID', value: payment.creditNoteId, isIdentifier: true },
  ].filter(hasRowValue);

  const collectionIdRow = getCollectionIdRow(payment);
  const collectionRows: DetailRow[] = [
    { label: 'Method', value: formatCollectionMethod(payment.collectionMethod) },
    ...(collectionIdRow ? [collectionIdRow] : []),
  ].filter(hasRowValue);

  const orderRows: DetailRow[] = [
    { label: 'Order ID', value: payment.orderId, isIdentifier: true },
    { label: 'Order Customer ID', value: payment.orderCustomerId, isIdentifier: true },
    { label: 'Customer ID', value: payment.customerId, isIdentifier: true },
    { label: 'User ID', value: payment.userId, isIdentifier: true },
  ].filter(hasRowValue);

  const productRows: DetailRow[] = [
    { label: 'Product', value: formatEnumLabel(payment.productId, productLabels) },
    { label: 'Product Entity ID', value: payment.productEntityId, isIdentifier: true },
  ].filter(hasRowValue);

  const metadataSections: DetailSection[] = [
    { title: 'Stripe', rows: stripeRows },
    { title: 'Collection Method', rows: collectionRows },
    { title: 'Order', rows: orderRows },
    { title: 'Product', rows: productRows },
  ].filter((section) => section.rows.length > 0);

  const renderSectionPanel = (section: DetailSection) => (
    <SectionPanel key={section.title}>
      <SectionTitle>{section.title}</SectionTitle>
      <DetailTable>
        <DetailRows rows={section.rows} />
      </DetailTable>
    </SectionPanel>
  );

  return (
    <Modal onDismiss={onDismiss} maxWidth="1040px" scrollable capHeight>
      <ModalHeader>
        <ModalTitle>Payment Details</ModalTitle>
        <CloseButton onClick={onDismiss} aria-label="Close payment details">
          &times;
        </CloseButton>
      </ModalHeader>
      <SectionTitle>Charge-backed payment</SectionTitle>
      <TwoColumnDetailTable>
        <DetailRows rows={paymentRows} />
      </TwoColumnDetailTable>
      {metadataSections.length > 0 ? <MetadataGrid>{metadataSections.map(renderSectionPanel)}</MetadataGrid> : null}
    </Modal>
  );
};

export const ClaimPayments: React.FC<ClaimPaymentsProps> = ({
  payments,
  stripePaymentTotal,
  stripePaymentTotalsByOrder = [],
  isLoading,
  error,
  activeOrderId,
  activeOrderStatusId,
  lastActiveOrderId,
  summaryOrderAmountRemaining,
  summaryOrderAmountRemainingOrderId,
  summaryOrderFilterOptions = [],
  orderFilterOptions = [],
  showAmountRemainingAccuracyWarning = true,
  onSummaryOrderChange,
}) => {
  const [selectedPayment, setSelectedPayment] = useState<ClaimStripePaymentDTO | null>(null);
  const defaultOrderId = activeOrderId || lastActiveOrderId || getLatestPaymentOrderId(payments);
  const orderOptions = buildOrderFilterOptions(payments, orderFilterOptions, activeOrderId, defaultOrderId);
  const summaryOrderOptions = buildOrderFilterOptions(payments, summaryOrderFilterOptions, activeOrderId, defaultOrderId);
  const activeOrderOption = orderOptions.find((option) => option.hasActiveOrder);
  const lastActiveOrderOption = !activeOrderId ? orderOptions.find((option) => option.hasDefaultOrder) : undefined;
  const defaultOrderOption = activeOrderOption || lastActiveOrderOption;
  const defaultOrderFilter = defaultOrderOption ? defaultOrderOption.value : ALL_ORDERS_FILTER;
  const activeSummaryOrderOption = summaryOrderOptions.find((option) => option.hasActiveOrder);
  const lastActiveSummaryOrderOption = !activeOrderId ? summaryOrderOptions.find((option) => option.hasDefaultOrder) : undefined;
  const defaultSummaryOrderOption = activeSummaryOrderOption || lastActiveSummaryOrderOption || summaryOrderOptions[0];
  const defaultSummaryOrderFilter = defaultSummaryOrderOption ? defaultSummaryOrderOption.value : '';
  const [selectedSummaryOrderFilter, setSelectedSummaryOrderFilter] = useState(defaultSummaryOrderFilter);
  const previousDefaultSummaryOrderFilter = useRef(defaultSummaryOrderFilter);
  const selectedSummaryOrderOption = summaryOrderOptions.find((option) => option.value === selectedSummaryOrderFilter);
  const summaryOrderLabel = selectedSummaryOrderOption ? selectedSummaryOrderOption.label : undefined;
  const summaryOrderId = getSummaryOrderIdForSelectedOption(selectedSummaryOrderOption, payments, activeOrderId, defaultOrderId);
  const summaryOrderPayment = summaryOrderId
    ? sortPayments(payments.filter((payment) => payment.orderId === summaryOrderId))[0]
    : undefined;
  const summaryOrderProductId =
    (selectedSummaryOrderOption && selectedSummaryOrderOption.productId) ||
    (summaryOrderPayment && summaryOrderPayment.productId);
  const canUseSummaryOrderLabel = !!(
    summaryOrderId &&
    summaryOrderLabel &&
    summaryOrderLabel !== formatOrderLabel(summaryOrderId)
  );
  const summaryOrderProduct = summaryOrderProductId
    ? formatEnumLabel(summaryOrderProductId, productLabels)
    : canUseSummaryOrderLabel
      ? summaryOrderLabel
      : formatEnumLabel(undefined, productLabels);
  const summaryOrderStatus = formatEnumLabel(
    (summaryOrderId === activeOrderId && activeOrderStatusId) || (summaryOrderPayment && summaryOrderPayment.orderStatusId),
    orderStatusLabels,
  );
  const summaryOrderHasKnownPaymentPlanInterestState = !!(
    selectedSummaryOrderOption && typeof selectedSummaryOrderOption.hasCollectiblePaymentPlanInterest === 'boolean'
  );
  const summaryOrderIncludesPaymentPlanInterest = !!(
    summaryOrderId &&
    (summaryOrderHasKnownPaymentPlanInterestState
      ? selectedSummaryOrderOption && selectedSummaryOrderOption.hasCollectiblePaymentPlanInterest
      : payments.some((payment) => payment.orderId === summaryOrderId && payment.paymentPlanId))
  );
  const summaryOrderAmountRemainingValue =
    summaryOrderId && (!summaryOrderAmountRemainingOrderId || summaryOrderAmountRemainingOrderId === summaryOrderId)
      ? summaryOrderAmountRemaining
      : undefined;
  const [selectedOrderFilter, setSelectedOrderFilter] = useState(defaultOrderFilter);
  const previousDefaultOrderFilter = useRef(defaultOrderFilter);
  const selectedOrderOption = orderOptions.find((option) => option.value === selectedOrderFilter);
  const selectedOrderIds =
    selectedOrderFilter === ALL_ORDERS_FILTER || !selectedOrderOption ? undefined : selectedOrderOption.orderIds;
  const filteredPayments = selectedOrderIds ? payments.filter((payment) => selectedOrderIds.includes(payment.orderId)) : payments;
  const sortedPayments = sortPayments(filteredPayments);
  const selectedPaymentTotal = getPaymentTotalForSelectedFilter(
    selectedOrderFilter,
    selectedOrderOption,
    stripePaymentTotal,
    stripePaymentTotalsByOrder,
  );
  const selectedPaymentTotalCharged = selectedPaymentTotal ? selectedPaymentTotal.totalCharged : undefined;
  const selectedPaymentTotalCaptured = selectedPaymentTotal ? selectedPaymentTotal.totalCaptured : undefined;
  const selectedPaymentCount = selectedPaymentTotal ? selectedPaymentTotal.paymentCount : undefined;
  const shouldShowCapturedTotalAsNotApplicable = selectedPaymentTotal
    ? selectedPaymentTotal.hasManualRefundOrDisputeHandling
    : false;
  const showOrderFilter = orderOptions.length > 0;
  const emptyPaymentScope = selectedOrderFilter === ALL_ORDERS_FILTER ? 'claim' : formatEmptyPaymentScope(selectedOrderOption);
  const emptyMessage = `No payments found for ${emptyPaymentScope}.`;

  useEffect(() => {
    if (previousDefaultOrderFilter.current !== defaultOrderFilter) {
      previousDefaultOrderFilter.current = defaultOrderFilter;
      setSelectedOrderFilter(defaultOrderFilter);
    }
  }, [defaultOrderFilter]);

  useEffect(() => {
    if (previousDefaultSummaryOrderFilter.current !== defaultSummaryOrderFilter) {
      previousDefaultSummaryOrderFilter.current = defaultSummaryOrderFilter;
      setSelectedSummaryOrderFilter(defaultSummaryOrderFilter);
    }
  }, [defaultSummaryOrderFilter]);

  useEffect(() => {
    const selectedOrderStillExists =
      selectedOrderFilter === ALL_ORDERS_FILTER || orderOptions.some((option) => option.value === selectedOrderFilter);

    if (!selectedOrderStillExists) {
      setSelectedOrderFilter(defaultOrderFilter);
    }
  }, [defaultOrderFilter, orderOptions, selectedOrderFilter]);

  useEffect(() => {
    const selectedSummaryOrderStillExists =
      !selectedSummaryOrderFilter || summaryOrderOptions.some((option) => option.value === selectedSummaryOrderFilter);

    if (!selectedSummaryOrderStillExists) {
      setSelectedSummaryOrderFilter(defaultSummaryOrderFilter);
    }
  }, [defaultSummaryOrderFilter, selectedSummaryOrderFilter, summaryOrderOptions]);

  useEffect(() => {
    if (onSummaryOrderChange) {
      onSummaryOrderChange(summaryOrderId, summaryOrderIncludesPaymentPlanInterest);
    }
  }, [onSummaryOrderChange, summaryOrderIncludesPaymentPlanInterest, summaryOrderId]);

  return (
    <>
      {summaryOrderId ? (
        <RepositCard
          flush
          title="Order"
          headerContent={
            summaryOrderOptions.length > 0 ? (
              <OrderFilter>
                <OrderFilterLabel>Order</OrderFilterLabel>
                <Select
                  name="claim-payments-summary-order-filter"
                  onChange={(value: string) => setSelectedSummaryOrderFilter(value)}
                  value={selectedSummaryOrderFilter}
                >
                  {summaryOrderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </OrderFilter>
            ) : null
          }
        >
          {showAmountRemainingAccuracyWarning ? (
            <OrderWarning>
              <OrderWarningText>
                Amount remaining for orders may be inaccurate until the collections squad migrate bank transfer history in to the
                system
              </OrderWarningText>
            </OrderWarning>
          ) : null}
          <InlineTable
            items={[
              { key: 'ID', value: <IdentifierValue>{summaryOrderId}</IdentifierValue> },
              { key: 'Product', value: summaryOrderProduct },
              { key: 'Status', value: summaryOrderStatus },
              {
                key: 'Order amount',
                value: formatMoney(selectedSummaryOrderOption ? selectedSummaryOrderOption.amount : undefined),
              },
              {
                key: summaryOrderIncludesPaymentPlanInterest ? 'Amount remaining (incl. interest)' : 'Amount remaining',
                value: formatMoney(summaryOrderAmountRemainingValue),
              },
              {
                key: 'Dispute handling',
                value: formatHandlingMode(selectedSummaryOrderOption ? selectedSummaryOrderOption.disputeHandling : undefined),
              },
              {
                key: 'Refund handling',
                value: formatHandlingMode(selectedSummaryOrderOption ? selectedSummaryOrderOption.refundHandling : undefined),
              },
            ]}
          />
        </RepositCard>
      ) : null}
      <RepositCard
        flush
        title="Payments"
        headerContent={
          showOrderFilter ? (
            <OrderFilter>
              <OrderFilterLabel>Order</OrderFilterLabel>
              <Select
                name="claim-payments-order-filter"
                onChange={(value: string) => setSelectedOrderFilter(value)}
                value={selectedOrderFilter}
              >
                <option value={ALL_ORDERS_FILTER}>All orders</option>
                {orderOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </OrderFilter>
          ) : null
        }
      >
        {selectedPayment ? <PaymentDetailsModal payment={selectedPayment} onDismiss={() => setSelectedPayment(null)} /> : null}
        {isLoading ? (
          <CardMessage>
            <P2>Loading payments...</P2>
          </CardMessage>
        ) : null}
        {!isLoading && error ? (
          <CardMessage>
            <P2>{error}</P2>
          </CardMessage>
        ) : null}
        {!isLoading && !error && sortedPayments.length === 0 ? (
          <CardMessage>
            <P2>{emptyMessage}</P2>
          </CardMessage>
        ) : null}
        {!isLoading && !error && sortedPayments.length > 0 ? (
          <>
            <PaymentSummaryTableContainer>
              <InlineTable
                items={[
                  { key: 'Total charged', value: formatMoney(selectedPaymentTotalCharged) },
                  {
                    key: 'Total captured',
                    value: formatCapturedTotal(selectedPaymentTotalCaptured, shouldShowCapturedTotalAsNotApplicable),
                  },
                  { key: 'Number of payments', value: formatCount(selectedPaymentCount) },
                ]}
              />
            </PaymentSummaryTableContainer>
            <TableScrollContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Td>Paid At</Td>
                    <Td>Amount</Td>
                    <Td>Status</Td>
                    <Td>Collection Method</Td>
                    <Td />
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedPayments.map((payment) => (
                    <Tr key={payment.id}>
                      <Td>{formatDateTime(payment.createdAt)}</Td>
                      <Td>
                        {formatMoney(payment.amount)}
                        {payment.amountCaptured !== payment.amount ? (
                          <AmountSecondaryText>Captured {formatMoney(payment.amountCaptured)}</AmountSecondaryText>
                        ) : null}
                      </Td>
                      <Td>{formatEnumLabel(payment.chargeStatusId, statusLabels)}</Td>
                      <Td>{formatCollectionMethod(payment.collectionMethod)}</Td>
                      <ActionCell>
                        <Button noArrow={true} mini="true" onClick={() => setSelectedPayment(payment)} type="button">
                          View details
                        </Button>
                      </ActionCell>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableScrollContainer>
          </>
        ) : null}
      </RepositCard>
    </>
  );
};

export default ClaimPayments;
